import { useState } from 'react'
import { KEY_PHASE, SPEC_KEYS } from '../../phases'
import type { EngagementView } from '../../stream/reducer'
import { formatWhen } from '../../time'

interface SpecDrawerProps {
  view: EngagementView
  open: boolean
  onOpen: () => void
  onClose: () => void

  /**
   * THE SPECIFICATION out of the bounded snapshot — not the snapshot envelope. Null until fetched.
   *
   * <p>`/state` returns `{engagement, specification, specVersion, seq}`, and this was handed the
   * whole envelope: every `snapshot[key]` below was `undefined`, and `summarise(undefined)` returns
   * the word "committed", so all fifteen rows rendered the same plausible placeholder (B-170).</p>
   */
  snapshot: Record<string, unknown> | null
}

type Pane = 'keys' | 'decisions' | 'json'

/**
 * THE SPECIFICATION, AS THE THIRD COLUMN — 34px collapsed, 420px expanded.
 *
 * <p>`Frontend/engagement.html` records why it is IN FLOW rather than an overlay, measured at
 * 1280×800: as `position:fixed` it removed 34px from the flex row when it opened, sliding the
 * centred conversation 17px toward the drawer about to cover it, and eighteen visible elements
 * lost their right 297px underneath it with no indication. Nothing is ever hidden behind it.</p>
 *
 * <p><b>THE TAB CARRIES THE LIVE KEY COUNT AND VERSION</b>, so "the specification panel updates
 * live" stays observable at 34px of width.</p>
 */
export function SpecDrawer({ view, open, onOpen, onClose, snapshot }: SpecDrawerProps)
  : React.JSX.Element {
  const [pane, setPane] = useState<Pane>('keys')
  const version = view.frozen !== null ? String(view.frozen['spec_version'] ?? '') : ''
  const count = view.spec.length

  // BOTH ARE RENDERED, ALWAYS, AND THE BODY'S `data-spec` DECIDES WHICH IS VISIBLE — which is how
  // `Frontend/engagement.html` does it. Rendering only one of them left the CSS's own
  // `.drawer{display:none}` in force with nothing to turn it off, so opening the panel UNMOUNTED
  // the tab and revealed a drawer that was still display:none. The panel vanished on click.
  return (
    <>
      <button className="spectab" onClick={onOpen} aria-expanded={open} aria-controls="specdrawer">
        <span className="pulse" aria-hidden="true" />
        <span className="lab">Specification</span>
        <span className="count">{count} keys{version.length > 0 ? ` · v${version}` : ''}</span>
      </button>

    <aside className="drawer" id="specdrawer" data-pane-show={pane} aria-label="Specification">
      <header>
        <h2 className="display">Specification</h2>
        <span className="ver">{count} keys{version.length > 0 ? ` · v${version}` : ''}</span>
        <button className="close" onClick={onClose}>Close</button>
      </header>

      <div className="seg" role="tablist" aria-label="Specification view">
        {(['keys', 'decisions', 'json'] as const).map((p) => (
          <button key={p} role="tab" aria-selected={pane === p} onClick={() => setPane(p)}>
            {p === 'keys' ? 'Committed keys' : p === 'decisions' ? 'Decisions' : 'JSON'}
          </button>
        ))}
      </div>

      <div className="scroll">
        <div data-pane="keys">
          {SPEC_KEYS.map((key) => {
            const committed = view.spec.includes(key)
            return (
              <div key={key} className={`specrow${committed ? '' : ' pending'}`}>
                <span className="k">{key}</span>
                <span className="ph">{KEY_PHASE[key]}</span>
                <span className="v">{committed ? summarise(snapshot?.[key]) : 'pending'}</span>
              </div>
            )
          })}
        </div>

        <div data-pane="decisions">
          {/* `decisions_recorded` is APPEND-ONLY across phases 3, 4, 5, 6 and the SIGN gate, and a
              re-entry SUPERSEDES rather than deletes — "a run that asked the user to ratify twice
              must show both". This is the home of the per-visit history. */}
          {view.timeline.filter((e) => e.kind === 'gate' && e.answer !== null).length === 0 ? (
            <p className="note">Nothing has been decided yet.</p>
          ) : (
            view.timeline.map((entry, i) => {
              if (entry.kind !== 'gate' || entry.answer === null) return null
              return (
                <div className="dec" key={i}>
                  {/* THE SAME RULE AS THE CARDS, rather than the raw instant. This printed
                      `2026-09-13T09:44:38.437Z` verbatim — correct, and not something a reader
                      parses. */}
                  <p className="when">
                    {formatWhen(entry.answer.at) ?? ''}
                    {entry.frame['phase'] !== undefined && ` · phase ${String(entry.frame['phase'])}`}
                  </p>
                  <p className="what">
                    <b>{String(entry.frame['gate'] ?? '')}</b>{' '}
                    {entry.answer.decision.length > 0 ? entry.answer.decision : 'answered in free text'}
                  </p>
                  {entry.answer.note !== null && entry.answer.note.length > 0 && (
                    <p className="detail">“{entry.answer.note}”</p>
                  )}
                </div>
              )
            })
          )}
        </div>

        <div data-pane="json">
          {snapshot === null ? (
            <p className="note">
              The snapshot has not been read yet. It is fetched when the stream reports a replay it
              could not complete, and on demand — it is not polled.
            </p>
          ) : (
            <pre className="json">{JSON.stringify(snapshot, null, 2)}</pre>
          )}
        </div>
      </div>

      <div className="foot">
        Every key here was committed by exactly one phase, and nothing in this panel is computed by
        the page.
      </div>
    </aside>
    </>
  )
}

/**
 * One line for a committed key.
 *
 * <p><b>THE PAGE RENDERS; IT DOES NOT COMPUTE.</b> A summary that derived a figure from the value
 * would be showing the reader something nobody committed, so this counts and names and does
 * nothing else.</p>
 */
/**
 * One committed key's value, in a column one line high.
 *
 * <p><b>`undefined` IS NOT A VALUE AND MUST NOT READ LIKE ONE.</b> This returned "committed" for a
 * key it had nothing for, which is a word a reader takes as a status — and for fifteen months of
 * this panel's life it was the only thing the column ever said, because the snapshot was being read
 * one level too high (B-170). It now says so.</p>
 */
function summarise(value: unknown): string {
  if (value === undefined) return 'not in the snapshot'
  if (value === null) return 'null'
  if (typeof value === 'string') return value.length > 120 ? `${value.slice(0, 117)}…` : value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return `${value.length} ${value.length === 1 ? 'entry' : 'entries'}`

  const keys = Object.keys(value as object)
  return keys.length === 0 ? 'committed' : keys.slice(0, 4).join(' · ')
}
